import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import { useParams, useNavigate } from "react-router-dom";

import api from "../../services/api";

const NDA_SIGNATURE_STATUS = {
  true: <span className="text-green-700 text-xs border border-green-600 p-1 rounded text-center px-3">✓ Signé</span>,
  false: <span className="text-red-500 px-3 text-xs border border-red-500 p-1 rounded text-center">✗ Non signé</span>,
};

const STATUS_SPAN = {
  PENDING: <span className="text-yellow-600 px-3 text-xs border border-yellow-600 p-1 rounded text-center">En attente</span>,
  ACCEPTED: <span className="text-green-600 px-3 text-xs border border-green-600 p-1 rounded text-center">Accepté - l&apos;utilisateur peut accéder aux documents</span>,
  ONGOING: <span className="text-orange-700 px-3 text-xs border border-orange-600 p-1 rounded text-center">En cours</span>,
  REFUSED: <span className="text-red-500 px-3 text-xs border border-red-500 p-1 rounded text-center">Refusé - l&apos;utilisateur ne peut pas accéder aux documents</span>,
  STOPPED: <span className="text-gray-500 px-3 text-xs border border-gray-500 p-1 rounded text-center">Arrêté</span>,
};

const View = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [nda, setNda] = useState();
  const [view, setView] = useState("infos");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { ok, data } = await api.get(`/nda/${id}`);
        if (!ok) return toast.error("Une erreur est survenue");

        setNda(data);
      } catch (error) {
        console.error(error);
        toast.error("Une erreur est survenue");
      }
    };
    fetchData();
  }, []);

  const handleChangeStatus = async (status) => {
    try {
      const res = await api.put(`/nda/${id}`, { status });
      if (res.ok) setNda(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Une erreur est survenue");
    }
  };

  const handleAccept = async () => {
    setLoading(true);
    try {
      const res = await api.put(`/nda/${id}`, { status: "ACCEPTED" });
      if (!res.ok) throw new Error("Une erreur est survenue");
      setNda(res.data);
    } catch (e) {
      console.error(e);
      toast.error("Une erreur est survenue");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce NDA ?")) return;

    try {
      const res = await api.delete(`/nda/${id}`);
      if (!res.ok) throw new Error("Une erreur est survenue");
      toast.success("NDA supprimé avec succès");
      navigate("/nda");
    } catch (e) {
      console.error(e);
      toast.error("Une erreur est survenue");
    }
  };

  const handleAnnouncementClick = () => {
    if (nda?.company_id && nda?.company_annonce_type) {
      const route = nda.company_annonce_type === "cession" ? "cession" : "levee";
      navigate(`/${route}/${nda.company_id}`);
    }
  };

  if (!nda) return <></>;

  return (
    <div className="flex flex-col p-8">
      <header className="w-full mb-8">
        <h1 className="text-3xl font-semibold text-black mb-2">NDA</h1>
      </header>

      <div className="flex justify-between flex-wrap items-center  mb-5 gap-3">
        <div className="flex gap-5">
          <button type="button" className={`rounded-lg px-4 py-2 ${view === "infos" ? "bg-gray-100" : "border border-secondary"}`} onClick={() => setView("infos")}>
            Informations
          </button>
          <button type="button" className={`rounded-lg px-4 py-2 ${view === "raw" ? "bg-gray-100" : "border border-secondary"}`} onClick={() => setView("raw")}>
            Données brutes
          </button>
        </div>
        <div className="flex gap-5">
          {nda.status === "PENDING" ? (
            <div className="flex gap-3">
              <button type="button" className="rounded-lg px-4 py-2 bg-green-500 text-white" onClick={handleAccept} disabled={loading}>
                {loading ? <div className="border-t border-white rounded-full w-6 h-6 animate-spin" /> : "Accepter"}
              </button>
              <button type="button" className="rounded-lg px-4 py-2 bg-red-500 text-white" onClick={() => handleChangeStatus("REFUSED")}>
                Refuser
              </button>
            </div>
          ) : (
            <div className="flex gap-2 items-center">
              <select className="select" value={nda.status} onChange={(e) => handleChangeStatus(e.target.value)}>
                <option value="PENDING">En attente</option>
                <option value="ACCEPTED">Acceptée</option>
                <option value="REFUSED">Refusée</option>
                <option value="ONGOING">En cours</option>
                <option value="STOPPED">Arrêtée</option>
              </select>
              <button
                type="button"
                onClick={handleDelete}
                className="px-3 py-2 text-sm text-red-600 border border-red-600 rounded-lg hover:bg-red-50 transition-colors"
                title="Supprimer le NDA">
                Supprimer
              </button>
            </div>
          )}
        </div>
      </div>

      {view === "infos" ? (
        <Infos nda={nda} setNda={setNda} onAnnouncementClick={handleAnnouncementClick} />
      ) : (
        <div className="col-span-2 bg-gray-900 rounded-lg p-5 flex flex-col gap-2">
          <pre className="text-white text-sm">{JSON.stringify({ ...nda }, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

const Infos = ({ nda, setNda, onAnnouncementClick }) => {
  const [loading, setLoading] = useState(false);

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAnnonceType = (type) => {
    switch (type) {
      case "levee":
        return "Levée de fonds";
      case "cession":
        return "Cession";
      default:
        return type || "-";
    }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2 border border-secondary rounded-lg p-5 flex flex-col gap-2">
        <p className="text-primary text-sm font-medium flex gap-3 items-center mb-3">Statut: {STATUS_SPAN[nda.status]}</p>
        <p className="text-primary text-sm font-medium">
          NDA signé: <span className="text-sm text-black">{NDA_SIGNATURE_STATUS[nda.nda_user_from_signed]}</span>
        </p>
        <p className="text-primary text-sm font-medium">
          Date de signature: <span className="text-sm text-black">{formatDateTime(nda.nda_user_from_signed_at)}</span>
        </p>
        {nda && nda.nda_pdf_url && (
          <a
            href={nda?.nda_pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white bg-blue-600 hover:bg-blue-700 text-sm font-medium py-1 px-2 rounded border border-blue-600 w-fit mt-2 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 text-center">
            Voir le NDA
          </a>
        )}
        {nda && !nda.nda_pdf_url && <span className="text-sm text-black mt-2">Le NDA n'a pas été enregistré</span>}
      </div>

      <div className="col-span-2 border border-secondary rounded-lg p-5 flex flex-col gap-2">
        <h2 className="text-xl font-medium text-primary mb-4">Informations de l'annonce</h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-primary text-sm font-medium">
              Nom de l'entreprise: <span className="text-sm text-black">{nda.company_name || "-"}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Type d'annonce: <span className="text-sm text-black">{formatAnnonceType(nda.company_annonce_type)}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Titre de l'annonce:{" "}
              {nda.company_annonce_title && nda.company_id ? (
                <button onClick={onAnnouncementClick} className="text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer">
                  {nda.company_annonce_title}
                </button>
              ) : (
                <span className="text-sm text-black">{nda.company_annonce_title || "-"}</span>
              )}
            </p>
            <p className="text-primary text-sm font-medium">
              Secteur: <span className="text-sm text-black">{nda.company_sector || "-"}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Adresse: <span className="text-sm text-black">{nda.company_address || "-"}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Rôle: <span className="text-sm text-black">{nda.company_role || "-"}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Créé le: <span className="text-sm text-black">{formatDateTime(nda.created_at)}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Mis à jour le: <span className="text-sm text-black">{formatDateTime(nda.updated_at)}</span>
            </p>
          </div>
          <div>
            <p className="text-primary text-sm font-medium">
              Prénom: <span className="text-sm text-black">{nda.user_to_first_name}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Nom: <span className="text-sm text-black">{nda.user_to_last_name}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              E-mail: <span className="text-sm text-black">{nda.user_to_email}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="col-span-2 border border-secondary rounded-lg p-5 flex flex-col gap-2">
        <h2 className="text-xl font-medium text-primary mb-4">Informations du demandeur</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-primary text-sm font-medium">
              Type d'entité: <span className="text-sm text-black">{nda.entityType === "morale" ? "Personne morale" : "Personne physique"}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              {nda.entityType === "morale" ? "Nom de l'entreprise" : "Nom"}: <span className="text-sm text-black">{nda.user_from_company_name || "-"}</span>
            </p>
            {nda.entityType === "morale" && (
              <p className="text-primary text-sm font-medium">
                Secteur: <span className="text-sm text-black">{nda.user_from_company_sector || "-"}</span>
              </p>
            )}
            {nda.entityType === "morale" && (
              <p className="text-primary text-sm font-medium">
                Rôle: <span className="text-sm text-black">{nda.user_from_company_role || "-"}</span>
              </p>
            )}
            <p className="text-primary text-sm font-medium">
              Adresse: <span className="text-sm text-black">{nda.user_from_company_address || "-"}</span>
            </p>
          </div>
          <div>
            <p className="text-primary text-sm font-medium">
              Prénom: <span className="text-sm text-black">{nda.user_from_first_name}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              Nom: <span className="text-sm text-black">{nda.user_from_last_name}</span>
            </p>
            <p className="text-primary text-sm font-medium">
              E-mail: <span className="text-sm text-black">{nda.user_from_email}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default View;
